import { useEditor } from '@tiptap/react';
import { Code as Key } from 'constants/keyboard';
import { useEventListener } from 'hooks/useEventListener';
import { useUnmountCallback } from 'hooks/useUnmountCallback/useUnmountCallback';
import isEqual from 'lodash.isequal';
import { useFlow } from 'modules/Editor/components/FlowCore';
import { RootState, store } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { MouseEvent, ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  WidgetBase,
  WidgetToolbar,
  getActiveWidgetSelectFrom,
  useEditor as useInfographEditor,
  useWidget,
} from 'widgets/sdk';
import { TOP_LAYER_Z_INDEX, useResponsiveGroup } from 'widgets/sdk';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';
import { TransparentImageBase } from 'widgets/sdk/TransparentImg';

import { getRawText } from '../common/TextBasedWidgets.helpers';
import { WidgetState } from '../common/TextBasedWidgets.types';
import '../common/prosemirror.css';
import { ReadOnlyTextEditor, TextEditor } from './TextEditor';
import { DEFAULT_CARET_COLOR } from './TextWidget.config';
import { handlePaste, wrapTextWithTag } from './TextWidget.helpers';
import { getMultiEditConfig } from './TextWidget.multiEdit';
import { TextWidgetBaseProps, TextWidgetData } from './TextWidget.types';
import { TextWidgetControlKeyboardShortcuts } from './TextWidgetControlKeyboardShortcuts';
import { extensions, getReadOnlyExtensions } from './TextWidgetExtensions';
import { DEFAULT_TEXT } from './defaultTextData';
import { TextWidgetToolbar } from './toolbar/TextWidgetToolbar';
import { useTextBoundingBox } from './useTextBoundingBox';

export const TextWidget = (): ReactElement => {
  const [widgetState, setNewWidgetState] = useState(WidgetState.Default);
  const { textTag, proseMirrorData, updateWidget, widgetId } = useWidget<TextWidgetData>();
  const { boundingBox } = useInfographEditor();
  const { responsiveGroupResize, hasUnsavedGroupMemberUpdates } = useResponsiveGroup(widgetId);

  // the unique id for the editor instance - used to reset the editor
  const [editorId, setEditorId] = useState(0);

  // A ref that keeps track of whether the user is typing in the editor / or updating the widget from the toolbar
  // The bounding box updates needs to know if the height is updated from typing in the editor or from the toolbar (or other e.g. keyboard)
  const isTypingRef = useRef(false);

  // The element that wraps the text editor
  const textWrapperRef = useRef<HTMLDivElement>(null);

  const isEditable = widgetState === WidgetState.Edit;
  const { getConnectedLineUpdatesToWidget } = useFlow();

  const editor = useEditor(
    {
      extensions,
      editorProps: {
        handleDOMEvents: {
          // Prevent text drag and drop
          drop: (_, e: Event) => {
            e.preventDefault();
            return false;
          },
        },
        handlePaste(this, view, _, slice) {
          return handlePaste(view, slice, textTag);
        },
      },
      content: proseMirrorData,
      editable: isEditable,
    },
    [editorId],
  );

  // reset the editor / history when the font family is changed from the apply font
  const destroyAndCreateNewEditor = () => {
    if (!editor) return;
    editor.destroy();
    setEditorId((editorId) => editorId + 1);
  };

  useEventListener('applyTheme', destroyAndCreateNewEditor);

  const dispatchUpdateWidget = useCallback(
    (options?: Partial<TextWidgetData>, force?: boolean, previousHeight?: number) => {
      const newProseMirrorData = editor?.getJSON();
      const hasForceUpdate = (typeof force === 'boolean' && force) || hasUnsavedGroupMemberUpdates();

      if (!newProseMirrorData) return;
      if (isEqual(newProseMirrorData, proseMirrorData) && !hasForceUpdate) {
        // the height can be updated even if the content is the same
        setTimeout(() => boundingBox.updateRect(), 0);
        return;
      }

      const heightPx = editor?.view.dom.clientHeight ?? 0;

      // In active state the size of the widget can be updated by e.g. updating the font size from the toolbar
      // in this case if the text widget is a group member - the other group members should be updated as well
      if (typeof previousHeight === 'number') {
        responsiveGroupResize({ height: heightPx, previousHeight });
      }

      const prevWidgetData = selectWidget(widgetId)(store.getState() as RootState);
      const prevHeightPx = prevWidgetData?.heightPx;

      // Get connected line widget updates to text only if the text widget height is updated
      let connectedLineWidgetUpdates: UpdateWidget[] = [];
      if (typeof prevHeightPx === 'number' && prevHeightPx !== heightPx) {
        connectedLineWidgetUpdates = getConnectedLineUpdatesToWidget(widgetId);
      }

      // Update the text widget, its fellow responsive group members, and connected line widgets in one commit.
      // Note: must be done in one commit to allow undo/redo to undo line widgets same time as text widget.
      updateWidget({ proseMirrorData: newProseMirrorData, heightPx, ...options }, connectedLineWidgetUpdates);
      // update the bounding box rect since the height can be updated
      setTimeout(() => boundingBox.updateRect(), 0);
    },
    [
      updateWidget,
      editor,
      widgetId,
      getConnectedLineUpdatesToWidget,
      proseMirrorData,
      boundingBox,
      responsiveGroupResize,
      hasUnsavedGroupMemberUpdates,
    ],
  );

  const multiEditConfig = useMemo(() => getMultiEditConfig(editor), [editor]);

  return (
    <>
      <WidgetToolbar multiEditConfig={multiEditConfig}>
        {editor && (
          <TextWidgetToolbar
            isTypingRef={isTypingRef}
            textWrapperRef={textWrapperRef}
            isEditable={isEditable}
            dispatchUpdateWidget={dispatchUpdateWidget}
            editor={editor}
            setWidgetState={setNewWidgetState}
          />
        )}
      </WidgetToolbar>
      <TextWidgetBase
        editor={editor}
        setNewWidgetState={setNewWidgetState}
        widgetState={widgetState}
        dispatchUpdateWidget={dispatchUpdateWidget}
        isTypingRef={isTypingRef}
        textWrapperRef={textWrapperRef}
        caretColor={editor?.getAttributes('textStyle')?.color || DEFAULT_CARET_COLOR}
      />
    </>
  );
};

const TextWidgetBase = memo(
  ({
    editor,
    widgetState,
    isTypingRef,
    textWrapperRef,
    caretColor,
    setNewWidgetState,
    dispatchUpdateWidget,
  }: TextWidgetBaseProps) => {
    const { proseMirrorData, widgetId, updateWidget, isWidgetSelected } = useWidget<TextWidgetData>();
    const { responsiveGroupResize } = useResponsiveGroup(widgetId);
    const {
      boundingBox,
      isWidgetFocused,
      enableEditorKeyboardShortcuts,
      disableEditorKeyboardShortcuts,
      hideContextMenu,
      setWidgetFocus,
      hideWidgetControl,
      showWidgetControl,
    } = useInfographEditor();
    const isEditable = widgetState === WidgetState.Edit;

    const { getConnectedLineUpdatesToWidget, stopFlowMode, startFlowMode } = useFlow();

    // keep these in refs to avoid running the undo/redo effect when these change
    // (we only want to run the undo/redo effect when the proseMirrorData has changed)
    const isWidgetSelectedRef = useRef(isWidgetSelected);
    const setWidgetStateRef = useRef(setNewWidgetState);

    // sync the refs with the latest values
    isWidgetSelectedRef.current = isWidgetSelected;
    setWidgetStateRef.current = setNewWidgetState;

    // update the widget state (default, active, edit)
    // Update the keyboard, focus, prosemirror editor as needed for each state change
    const setWidgetState = useCallback(
      (newWidgetState: WidgetState) => {
        setNewWidgetState(newWidgetState);

        const isActive = newWidgetState === WidgetState.Active;
        const isDefault = newWidgetState === WidgetState.Default;
        const isEditable = newWidgetState === WidgetState.Edit;

        // When the widget is in edit state the editor is focused
        // Reset the focus to the widget when state changes from edit -> active for widget keyboard shortcuts to work
        if (isActive && !isWidgetFocused()) setWidgetFocus();

        if (isActive || isDefault) {
          // The editor should never be editable when the widget is not in edit state
          if (editor?.isEditable && !editor.isDestroyed) editor?.setEditable(false);

          // keyboard shortcuts should be enabled when the widget is not in edit state
          enableEditorKeyboardShortcuts();

          // if the text is empty, set it to the default text when exiting edit mode
          if (editor?.getText() === '') {
            // similar to a toolbar change, when the default text is added it can change the height of the widget
            isTypingRef.current = false;

            // reset the letterSpacing when grouped widget default text is added for group text widgets
            // should be before measuring the height (since the letterSpacing can affect the height)
            const isGroupWidget = groupIdCache.hasParentWidget(widgetId);
            const letterSpacing = textWrapperRef.current?.style?.letterSpacing;
            const hasLetterSpacing = letterSpacing && letterSpacing !== '0em';
            if (hasLetterSpacing && isGroupWidget) textWrapperRef.current!.style.letterSpacing = '0em';

            const previousHeight = editor?.view.dom.clientHeight ?? 0;

            editor.chain().insertContent(DEFAULT_TEXT).run();

            const heightPx = editor?.view.dom.clientHeight ?? 0;
            const newProseMirrorData = editor?.getJSON();

            const widgetData: Partial<TextWidgetData> = { proseMirrorData: newProseMirrorData, heightPx };
            if (isGroupWidget && hasLetterSpacing) widgetData.letterSpacing = 0;

            responsiveGroupResize({ height: heightPx, previousHeight });

            const connectedLineWidgetUpdates = getConnectedLineUpdatesToWidget(widgetId);
            updateWidget(widgetData, connectedLineWidgetUpdates);
          }

          // default/active should have no text selection
          if (!editor?.isDestroyed) editor?.commands.blur();

          showWidgetControl();
        }

        if (isEditable) {
          // Start listening to update connected line widgets when text widget is updated.
          startFlowMode();

          // focus the editor if its not focused
          if (editor && !editor.isFocused && !editor?.isDestroyed) {
            editor.setEditable(true);
            editor.commands.focus('all');
          }

          hideWidgetControl();

          // disable keyboard shortcuts when the widget is in edit state
          // The duplicate shortcut is still allowed
          disableEditorKeyboardShortcuts({ allowedKeyboardKeys: [Key.D] });
        }
      },
      // no need to include setNewWidgetState here since it is a setter function
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        editor,
        disableEditorKeyboardShortcuts,
        enableEditorKeyboardShortcuts,
        updateWidget,
        getConnectedLineUpdatesToWidget,
        widgetId,
        startFlowMode,
      ],
    );

    const onClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        // If click target (target) matches widget container (currentTarget), keep it in active state
        // otherwise the text content was clicked so enter edit state
        if (widgetState === WidgetState.Active && e.target !== e.currentTarget) setWidgetState(WidgetState.Edit);
        if (widgetState === WidgetState.Default) setWidgetState(WidgetState.Active);
      },
      [widgetState, setWidgetState],
    );

    // This effect updates the widget state when the widget is selected or deselected
    useEffect(() => {
      // If the widget is not selected anymore, reset the widget state to default
      if (!isWidgetSelected && widgetState !== WidgetState.Default) {
        setWidgetState(WidgetState.Default);
      }

      const activeWidgetSelectFrom = getActiveWidgetSelectFrom();
      // If the widget is selected and the widget state is default, set the widget state to active
      // selection click clashes with the WidgetBase onClick event (without this text will go straigt to edit mode in one click)
      if (isWidgetSelected && widgetState === WidgetState.Default && activeWidgetSelectFrom !== 'click-select') {
        setWidgetState(WidgetState.Active);
      }
      // run this effect only when widgetState, isWidgetSelected changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWidgetSelected, widgetState]);

    // if the widget is deleted ensure that the keyboard shortcuts are enabled again
    useEffect(() => () => enableEditorKeyboardShortcuts(), [enableEditorKeyboardShortcuts]);

    // This effect updates tiptap editor content if there is change via external action like undo/redo
    // For large document, it may require more optimization later on
    useEffect(() => {
      const shouldResetEditorContent = editor && proseMirrorData && !isEqual(editor.getJSON(), proseMirrorData);

      // the editor can be destroyed when changed from apply/font color - if it is destroyed we don't need to update the content here
      if (shouldResetEditorContent && !editor?.isDestroyed) {
        editor.commands.setContent(proseMirrorData);

        // reset the widget state to active since the selection is lost when the editor content is reset
        if (isWidgetSelectedRef.current) {
          setWidgetStateRef?.current(WidgetState.Active);
          // update the rect since the height can be updated from the new content
          setTimeout(() => boundingBox.updateRect(), 0);
          // blur the editor to remove the selection for Safari
          editor?.commands.blur();
        }
      }
      // run this effect only when the prosemirror data changes
    }, [proseMirrorData, boundingBox, editor]);

    const { customOnResizeStart, customOnResize, customOnResizeEnd, customOnRotateStart, customOnDragEnd } =
      useTextBoundingBox({ setWidgetState, editor, isEditable, isDefault: widgetState === WidgetState.Default });

    const onBlur = useCallback(() => {
      // if the text is empty, set it to the default text when exiting edit mode in setWidgetState
      if (editor?.getText() === '') return;
      isTypingRef.current = false;

      dispatchUpdateWidget();

      // Stop listening to stop updating connected line widgets when text widget is updated.
      stopFlowMode();
    }, [dispatchUpdateWidget, stopFlowMode, editor, isTypingRef]);

    useUnmountCallback(() => {
      if (isWidgetSelectedRef.current) {
        if (editor?.getText() === '') return;
        isTypingRef.current = false;

        dispatchUpdateWidget();
      }
    });

    // If widget in edit mode, default browser context menu should be displayed
    const onContextMenu = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        if (isEditable) {
          e.stopPropagation();
          hideContextMenu();
          return false;
        }
      },
      [isEditable, hideContextMenu],
    );

    return (
      <>
        <WidgetBase
          onClick={onClick}
          onContextMenu={onContextMenu}
          onResizeStart={customOnResizeStart}
          onResizeEnd={customOnResizeEnd}
          onResize={customOnResize}
          onRotateStart={customOnRotateStart}
          onDragEnd={customOnDragEnd}
          customZIndex={isEditable ? TOP_LAYER_Z_INDEX : undefined}
        >
          {isWidgetSelected && editor && (
            <TextWidgetControlKeyboardShortcuts
              editor={editor}
              setWidgetState={setWidgetState}
              widgetState={widgetState}
              dispatchUpdateWidget={dispatchUpdateWidget}
            />
          )}
          {editor && (
            <TextEditor
              textWrapperRef={textWrapperRef}
              onBlur={onBlur}
              isEditable={isEditable}
              editor={editor}
              isTypingRef={isTypingRef}
              caretColor={caretColor}
            />
          )}
        </WidgetBase>
      </>
    );
  },
);
TextWidgetBase.displayName = 'TextWidgetBase';

export const ReadOnlyTextWidget = (): ReactElement => {
  const {
    widgetId,
    proseMirrorData,
    textTag,
    effect: { type } = { type: '' },
    renderType,
  } = useWidget<TextWidgetData>();
  const disableTabbability = renderType === ReadOnlyRenderType.ThumbnailReadOnly;
  const extensions = getReadOnlyExtensions(
    textTag,
    disableTabbability,
    renderType === ReadOnlyRenderType.ExportStructureTreeList,
  );
  const editor = useEditor({ extensions, content: proseMirrorData, editable: false }, [proseMirrorData, textTag]);
  const hasEffect = !!type;

  // for non-paragraph text
  // wrap the text in the correct header tag (h1 - h6 | title)
  // prevent wrapping the text tag in the PM for scroll issues - TODO remove this when the scroll issue is fixed
  if (editor && !disableTabbability) wrapTextWithTag(editor, textTag);

  return (
    <ReadOnlyWidgetBase>
      {/* Text with effects are tagged as figure/image in the PDF Export */}
      {hasEffect && !disableTabbability && (
        <TransparentImageBase altText={getRawText(proseMirrorData, undefined, false)} id={widgetId} />
      )}
      <ReadOnlyTextEditor editor={editor} />
    </ReadOnlyWidgetBase>
  );
};
